import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDownRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchFeaturedProducts, fetchLookbook } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Marquee } from "../components/Marquee";
import { AppDownloadSection } from "../components/AppDownloadSection";
import { JsonLd, organizationSchema, websiteSchema } from "../components/JsonLd";

export default function Home() {
    const { t } = useTranslation();
    const [featured, setFeatured] = useState([]);
    const [look, setLook] = useState(null);

    useEffect(() => {
        fetchFeaturedProducts().then(setFeatured).catch(() => {});
        fetchLookbook().then(setLook).catch(() => {});
    }, []);

    const heroImg = "/hero-savnt.jpg";
    const marqueeItems = t("home.marquee", { returnObjects: true });

    return (
        <div data-testid="home-page" className="pt-16">
            <JsonLd data={organizationSchema()} />
            <JsonLd data={websiteSchema()} />
            {/* HERO */}
            <section className="relative min-h-[92vh] grain overflow-hidden">
                <div className="absolute inset-0">
                    <img src={heroImg} alt="SAVNT — For The Chosen" className="h-full w-full object-cover object-center opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0C0C0C]/30 via-[#0C0C0C]/10 to-[#0C0C0C]" />
                </div>

                <div className="container-rail relative z-10 pt-32 sm:pt-40 pb-16 grid grid-cols-12 gap-6 min-h-[92vh]">
                    <div className="col-span-12 lg:col-span-8 flex flex-col justify-between">
                        <div className="space-y-8">
                            <div className="text-overline text-[#A3A3A3]" data-testid="hero-overline">
                                {t("home.hero_overline")}
                            </div>
                            <h1 className="text-display text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.88]">
                                {t("home.hero_title_part_1")} <span className="text-display-italic text-[#A3A3A3]">{t("home.hero_title_italic")}</span>
                                <br />
                                {t("home.hero_title_part_2")}
                            </h1>
                            <p className="max-w-md text-[#A3A3A3] text-base leading-relaxed">
                                {t("home.hero_body")} <span className="text-[#F4F4F0]">{t("home.hero_body_strong")}</span>
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 mt-12">
                            <Link to="/shop" data-testid="hero-shop-cta" className="btn-primary">
                                {t("home.hero_cta_shop")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </Link>
                            <Link to="/lookbook" data-testid="hero-lookbook-cta" className="btn-ghost">
                                {t("home.hero_cta_lookbook")}
                            </Link>
                        </div>
                    </div>

                    <div className="hidden lg:flex col-span-4 flex-col justify-end items-end gap-3 text-right">
                        <div className="text-overline text-[#666]">{t("home.studio_note_label")}</div>
                        <p className="text-display text-3xl max-w-[280px] text-right leading-tight">
                            "{t("home.studio_note_quote")}<br /><span className="text-display-italic">{t("home.studio_note_italic")}"</span>
                        </p>
                        <div className="text-overline text-[#A3A3A3]">{t("home.studio_note_credit")}</div>
                    </div>
                </div>

                <div className="absolute bottom-6 left-0 right-0 z-10">
                    <div className="container-rail flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-[#A3A3A3] font-display">
                        <div>{t("home.hero_footer_pieces")}</div>
                        <div className="hidden sm:flex items-center gap-2">{t("home.hero_footer_scroll")} <ArrowDownRight className="h-3 w-3" /></div>
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <Marquee items={marqueeItems} />

            {/* PHILOSOPHY */}
            <section className="container-rail py-32 grid grid-cols-12 gap-10 items-start">
                <div className="col-span-12 md:col-span-3">
                    <div className="text-overline text-[#A3A3A3]">{t("home.philosophy_label")}</div>
                </div>
                <div className="col-span-12 md:col-span-7">
                    <h2 className="text-display text-4xl sm:text-5xl md:text-6xl leading-[1] mb-8">
                        {t("home.philosophy_title_1")} <span className="text-display-italic">{t("home.philosophy_title_italic")}</span>.
                    </h2>
                    <p className="text-[#A3A3A3] text-lg leading-relaxed max-w-2xl">
                        {t("home.philosophy_body")} <span className="text-[#F4F4F0]">{t("home.philosophy_body_strong")}</span>
                    </p>
                </div>
            </section>

            {/* FEATURED */}
            <section className="container-rail pb-24">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                    <div>
                        <div className="text-overline text-[#A3A3A3] mb-3">{t("home.featured_label")}</div>
                        <h2 className="text-display text-4xl sm:text-5xl md:text-6xl">{t("home.featured_title")}</h2>
                    </div>
                    <Link to="/shop" data-testid="featured-view-all" className="text-overline text-[#A3A3A3] hover:text-[#F4F4F0] inline-flex items-center gap-2">
                        {t("home.view_all")} <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                    {featured.slice(0, 4).map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            </section>

            {/* LOOKBOOK STRIP */}
            {look && (
                <section className="container-rail py-24">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-6 row-span-2">
                            <Link to="/lookbook" className="block relative aspect-[3/4] overflow-hidden bg-[#141414] border border-white/[0.06] group">
                                <img src={look.images[0]} alt="Lookbook" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
                                <div className="absolute bottom-6 left-6">
                                    <div className="text-overline text-[#EDEDE6]/80">{t("home.lookbook_label")}</div>
                                    <div className="text-display text-3xl sm:text-4xl mt-2 text-[#F4F4F0]">{look.title}</div>
                                </div>
                            </Link>
                        </div>
                        <div className="col-span-6 md:col-span-3">
                            <div className="relative aspect-square overflow-hidden bg-[#141414] border border-white/[0.06]">
                                <img src={look.images[1]} alt="Lookbook" className="h-full w-full object-cover" />
                            </div>
                        </div>
                        <div className="col-span-6 md:col-span-3">
                            <div className="relative aspect-square overflow-hidden bg-[#141414] border border-white/[0.06]">
                                <img src={look.images[2]} alt="Lookbook" className="h-full w-full object-cover" />
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            <div className="relative aspect-[16/9] overflow-hidden bg-[#141414] border border-white/[0.06]">
                                <img src={look.images[3]} alt="Lookbook" className="h-full w-full object-cover" />
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* APP DOWNLOAD */}
            <AppDownloadSection />

            {/* SPLIT EDITORIAL */}
            <section className="container-rail py-32">
                <div className="grid grid-cols-12 gap-10 items-center">
                    <div className="col-span-12 md:col-span-6">
                        <div className="text-overline text-[#A3A3A3] mb-6">{t("home.atelier_label")}</div>
                        <h2 className="text-display text-5xl sm:text-6xl md:text-7xl leading-[0.92] mb-8">
                            {t("home.atelier_title_1")} <br />
                            <span className="text-display-italic">{t("home.atelier_title_italic")}</span>
                        </h2>
                        <p className="text-[#A3A3A3] text-base leading-relaxed mb-2 max-w-xl">
                            {t("home.atelier_body")}
                        </p>
                        <p className="text-[#F4F4F0] text-base mb-8 max-w-xl">
                            {t("home.atelier_body_strong")}
                        </p>
                        <Link to="/about" data-testid="atelier-cta" className="btn-ghost">{t("home.atelier_cta")}</Link>
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <div className="relative aspect-[4/5] overflow-hidden bg-[#141414] border border-white/[0.06]">
                            <img
                                src="/savnt-bottom-image.jpg"
                                alt="Atelier"
                                className="h-full w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
